import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ScrollReveal from "@/components/ScrollReveal";
import TickingNumber from "@/components/TickingNumber";
import { toast } from "sonner";
import {
  Shield,
  TrendingUp,
  ArrowDownLeft,
  RefreshCw,
  CheckCircle,
  Info,
  Gift,
  Lock,
  Clock,
  BarChart2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { MOCK_CUSTODY_ORDERS, MOCK_ASSETS, formatUSDT } from "@/lib/mockData";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import KLineCanvas from "@/components/KLineCanvas";

const C = {
  bg: "#0a0b0e",
  card: "#0f1117",
  card2: "#161a22",
  border: "rgba(255,255,255,0.07)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  dim: "#64748b",
  primary: "#22c55e",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
};

function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info
          size={11}
          className="inline-block ml-1 cursor-help flex-shrink-0"
          style={{ color: C.dim }}
        />
      </TooltipTrigger>
      <TooltipContent
        className="max-w-xs text-xs"
        style={{
          background: C.card2,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

function DepositDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState(1000);
  const [step, setStep] = useState<"form" | "pending" | "success">("form");
  const dailyEarning = amount * 0.0173;
  const monthlyEarning = amount * 0.52;
  const handleClose = () => {
    setStep("form");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>托管存入</DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <div
                className="rounded-lg p-3 flex items-center justify-between"
                style={{
                  background: "rgba(34,197,94,0.06)",
                  border: "1px solid rgba(34,197,94,0.18)",
                }}
              >
                <div>
                  <div
                    className="text-[10px] uppercase tracking-wider mb-0.5"
                    style={{ color: C.dim }}
                  >
                    月化收益率
                  </div>
                  <div
                    className="mono-num font-bold text-2xl"
                    style={{ color: C.primary }}
                  >
                    52%
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] mb-0.5" style={{ color: C.dim }}>
                    日化约
                  </div>
                  <div
                    className="mono-num font-bold text-lg"
                    style={{ color: C.amber }}
                  >
                    1.73%
                  </div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-2">
                  <span style={{ color: C.muted }}>
                    存入金额 ($)
                    <InfoTip text="最低存入 $100，T+1 开始计息" />
                  </span>
                  <span className="mono-num" style={{ color: C.muted }}>
                    可用: ${formatUSDT(MOCK_ASSETS.usdt.available)}
                  </span>
                </div>
                <div className="flex gap-2 mb-2">
                  {[500, 1000, 3000, 5000].map(v => (
                    <button
                      key={v}
                      onClick={() => setAmount(v)}
                      className="flex-1 py-1.5 rounded text-xs mono-num font-semibold"
                      style={{
                        background:
                          amount === v ? "rgba(34,197,94,0.10)" : C.card2,
                        border: `1px solid ${amount === v ? "rgba(34,197,94,0.35)" : C.border}`,
                        color: amount === v ? C.primary : C.muted,
                      }}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(Number(e.target.value))}
                  className="w-full px-3 py-2.5 rounded-lg text-sm mono-num outline-none"
                  style={{
                    background: C.card2,
                    border: `1px solid ${C.border}`,
                    color: C.text,
                  }}
                />
              </div>
              <div
                className="rounded-lg p-3 space-y-2 text-xs"
                style={{ background: C.card2, border: `1px solid ${C.border}` }}
              >
                <div className="font-semibold mb-1" style={{ color: C.muted }}>
                  预估收益
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>每日收益</span>
                  <span
                    className="mono-num font-semibold"
                    style={{ color: C.green }}
                  >
                    ${formatUSDT(dailyEarning)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>月收益预估</span>
                  <span
                    className="mono-num font-semibold"
                    style={{ color: C.amber }}
                  >
                    ${formatUSDT(monthlyEarning)}
                  </span>
                </div>
              </div>
              <div
                className="rounded-lg p-3 text-[10px] space-y-1.5"
                style={{
                  background: "rgba(34,197,94,0.04)",
                  border: "1px solid rgba(34,197,94,0.12)",
                }}
              >
                {[
                  "T+1 开始计息，赎回需 T+1 到账",
                  "收益 70% USDT + 30% TSK 代币发放",
                  "持有 AI 智能体可享额外 10-30% 收益加成",
                  "无锁仓期，随时可申请赎回",
                ].map((rule, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-1.5"
                    style={{ color: C.muted }}
                  >
                    <div
                      className="w-1 h-1 rounded-full mt-1 shrink-0"
                      style={{ background: C.primary }}
                    />
                    {rule}
                  </div>
                ))}
              </div>
              <button
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary"
                onClick={() => {
                  setStep("pending");
                  setTimeout(() => setStep("success"), 2000);
                }}
              >
                确认存入 ${formatUSDT(amount)}
              </button>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-4"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <RefreshCw
                  size={24}
                  className="animate-spin"
                  style={{ color: C.primary }}
                />
              </div>
              <p className="text-sm" style={{ color: C.muted }}>
                等待链上确认...
              </p>
            </motion.div>
          )}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-4"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <CheckCircle size={28} style={{ color: C.green }} />
              </div>
              <div className="text-center">
                <p className="font-semibold mb-1">存入成功！</p>
                <p className="text-sm" style={{ color: C.muted }}>
                  明日 10:00 起开始计息
                </p>
              </div>
              <button
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                完成
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

function RedeemDialog({
  open,
  onClose,
  order,
}: {
  open: boolean;
  onClose: () => void;
  order: (typeof MOCK_CUSTODY_ORDERS)[0] | null;
}) {
  const [step, setStep] = useState<"confirm" | "pending" | "success">(
    "confirm"
  );
  if (!order) return null;
  const handleClose = () => {
    setStep("confirm");
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(239,68,68,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.amber }}>申请赎回</DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "confirm" && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              <div
                className="rounded-lg p-3 space-y-2 text-xs"
                style={{ background: C.card2, border: `1px solid ${C.border}` }}
              >
                <div className="flex justify-between">
                  <span style={{ color: C.muted }}>托管本金</span>
                  <span className="mono-num font-medium">
                    ${formatUSDT(order.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.muted }}>累计收益</span>
                  <span className="mono-num font-medium">
                    ${formatUSDT(order.totalEarned)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.muted }}>到账时间</span>
                  <span className="mono-num font-medium">T+1 工作日</span>
                </div>
                <div className="h-px" style={{ background: C.border }} />
                <div className="flex justify-between font-semibold">
                  <span style={{ color: C.muted }}>预计到账</span>
                  <span className="mono-num" style={{ color: C.green }}>
                    ${formatUSDT(order.amount + order.rewardSplit.totalUsdt)}
                  </span>
                </div>
              </div>
              <div
                className="rounded-lg p-3 text-[10px] flex items-start gap-2"
                style={{
                  background: "rgba(239,68,68,0.06)",
                  color: C.red,
                  border: "1px solid rgba(239,68,68,0.15)",
                }}
              >
                <Clock size={11} className="mt-0.5 shrink-0" />
                <span>赎回申请提交后，本金和收益将在 T+1 工作日内到账</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={handleClose}
                  style={{
                    borderColor: C.border,
                    color: C.muted,
                    background: "transparent",
                  }}
                >
                  取消
                </Button>
                <button
                  className="flex-1 py-2.5 rounded-lg font-bold text-sm"
                  style={{ background: C.red, color: "white" }}
                  onClick={() => {
                    setStep("pending");
                    setTimeout(() => setStep("success"), 1500);
                  }}
                >
                  确认赎回
                </button>
              </div>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-3"
            >
              <RefreshCw
                size={28}
                className="animate-spin"
                style={{ color: C.amber }}
              />
              <p className="text-sm" style={{ color: C.muted }}>
                处理中...
              </p>
            </motion.div>
          )}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-3"
            >
              <CheckCircle size={36} style={{ color: C.green }} />
              <p className="font-semibold">赎回申请已提交</p>
              <p className="text-xs text-center" style={{ color: C.muted }}>
                资金将在 T+1 工作日内到账
              </p>
              <button
                className="w-full py-2.5 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                完成
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// ===== 领取收益弹窗 =====
function ClaimDialog({
  open,
  onClose,
  order,
}: {
  open: boolean;
  onClose: () => void;
  order: (typeof MOCK_CUSTODY_ORDERS)[0] | null;
}) {
  const [step, setStep] = useState<"confirm" | "pending" | "success">(
    "confirm"
  );
  if (!order) return null;
  const handleClose = () => {
    setStep("confirm");
    onClose();
  };
  const pendingUsdt = order.rewardSplit.pendingUsdt;
  const pendingAif = order.rewardSplit.pendingAif;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>领取收益</DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "confirm" && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <div
                className="rounded-lg p-4 space-y-3"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <div
                  className="text-xs font-semibold mb-1"
                  style={{ color: C.muted }}
                >
                  本次可领取收益
                </div>
                {/* USDT */}
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{
                    background: "rgba(34,197,94,0.06)",
                    border: "1px solid rgba(34,197,94,0.18)",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ background: "rgba(34,197,94,0.12)" }}
                    >
                      <span
                        className="text-xs font-bold"
                        style={{ color: C.green }}
                      >
                        $
                      </span>
                    </div>
                    <span className="text-sm font-semibold">USDT</span>
                  </div>
                  <span
                    className="mono-num font-bold text-base"
                    style={{ color: C.green }}
                  >
                    +${formatUSDT(pendingUsdt)}
                  </span>
                </div>
                {/* TSK */}
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{
                    background: "rgba(34,197,94,0.06)",
                    border: "1px solid rgba(34,197,94,0.18)",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ background: "rgba(34,197,94,0.12)" }}
                    >
                      <span
                        className="text-xs font-bold"
                        style={{ color: C.primary }}
                      >
                        A
                      </span>
                    </div>
                    <span className="text-sm font-semibold">TSK</span>
                  </div>
                  <span
                    className="mono-num font-bold text-base"
                    style={{ color: C.primary }}
                  >
                    +{formatUSDT(pendingAif)}
                  </span>
                </div>
              </div>
              <div
                className="rounded-lg p-3 text-[10px] flex items-start gap-1.5"
                style={{
                  background: "rgba(34,197,94,0.04)",
                  border: "1px solid rgba(34,197,94,0.12)",
                  color: C.muted,
                }}
              >
                <Gift
                  size={11}
                  className="mt-0.5 shrink-0"
                  style={{ color: C.primary }}
                />
                <span>
                  收益将直接到账至您的平台钱包，USDT 可提现，TSK
                  可用于手续费抵扣
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={handleClose}
                  style={{
                    borderColor: C.border,
                    color: C.muted,
                    background: "transparent",
                  }}
                >
                  取消
                </Button>
                <button
                  className="flex-1 py-2.5 rounded-lg font-bold text-sm btn-primary"
                  onClick={() => {
                    setStep("pending");
                    setTimeout(() => setStep("success"), 1500);
                  }}
                >
                  确认领取
                </button>
              </div>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-4"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <RefreshCw
                  size={24}
                  className="animate-spin"
                  style={{ color: C.primary }}
                />
              </div>
              <p className="text-sm" style={{ color: C.muted }}>
                处理中...
              </p>
            </motion.div>
          )}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-4"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <CheckCircle size={28} style={{ color: C.green }} />
              </div>
              <div className="text-center">
                <p className="font-semibold mb-2">领取成功！</p>
                <div className="space-y-1 text-sm">
                  <p>
                    <span style={{ color: C.muted }}>USDT：</span>
                    <span
                      className="mono-num font-bold"
                      style={{ color: C.green }}
                    >
                      +${formatUSDT(pendingUsdt)}
                    </span>
                  </p>
                  <p>
                    <span style={{ color: C.muted }}>TSK：</span>
                    <span
                      className="mono-num font-bold"
                      style={{ color: C.primary }}
                    >
                      +{formatUSDT(pendingAif)} TSK
                    </span>
                  </p>
                </div>
              </div>
              <button
                className="w-full py-2.5 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                完成
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

const EARNINGS_HISTORY = [
  { date: "2025-04-07", usdt: 28.04, aif: 12.02, total: 40.06 },
  { date: "2025-04-06", usdt: 28.04, aif: 12.02, total: 40.06 },
  { date: "2025-04-05", usdt: 28.04, aif: 12.02, total: 40.06 },
  { date: "2025-04-04", usdt: 28.04, aif: 12.02, total: 40.06 },
  { date: "2025-04-03", usdt: 28.04, aif: 12.02, total: 40.06 },
  { date: "2025-04-02", usdt: 17.3, aif: 7.42, total: 24.72 },
  { date: "2025-04-01", usdt: 17.3, aif: 7.42, total: 24.72 },
  { date: "2025-03-31", usdt: 25.42, aif: 10.9, total: 36.32 },
  { date: "2025-03-30", usdt: 25.42, aif: 10.9, total: 36.32 },
  { date: "2025-03-29", usdt: 25.42, aif: 10.9, total: 36.32 },
];

export default function CustodyTrading() {
  const [activeTab, setActiveTab] = useState<"active" | "history" | "earnings">(
    "active"
  );
  const [depositOpen, setDepositOpen] = useState(false);
  const [redeemOrder, setRedeemOrder] = useState<
    (typeof MOCK_CUSTODY_ORDERS)[0] | null
  >(null);
  const [claimOrder, setClaimOrder] = useState<
    (typeof MOCK_CUSTODY_ORDERS)[0] | null
  >(null);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  const activeOrders = MOCK_CUSTODY_ORDERS.filter(o => o.status === "ongoing");
  const completedOrders = MOCK_CUSTODY_ORDERS.filter(
    o => o.status === "completed"
  );
  const totalCustody = activeOrders.reduce((s, o) => s + o.amount, 0);
  const totalEarned = MOCK_CUSTODY_ORDERS.reduce(
    (s, o) => s + o.totalEarned,
    0
  );
  const todayEarned = activeOrders.reduce((s, o) => s + o.todayEarned, 0);

  return (
    <div className="pt-[80px] relative">
      {/* BTC K线背景 */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <KLineCanvas height={500} opacity={0.3} />
      </div>

      {/* Header */}
      <ScrollReveal
        direction="up"
        distance={18}
        duration={0.45}
        className="px-4 pt-5 pb-5"
        style={{ background: "#0d1117", borderBottom: `1px solid ${C.border}` }}
      >
        <h1 className="text-xl font-bold mb-0.5">
          <span style={{ color: C.primary }}>AI</span>
          <span style={{ color: C.text }}> 量化托管</span>
        </h1>
        <p className="text-xs mb-4" style={{ color: C.muted }}>
          月化 52% · 日化 1.73% · T+1 计息
        </p>
        <div className="grid grid-cols-3 gap-2">
          {[
            {
              icon: <Lock size={12} />,
              label: "托管总量",
              value: `$${formatUSDT(totalCustody)}`,
              sub: "",
              color: C.primary,
              tip: "当前所有进行中托管订单的本金总和",
            },
            {
              icon: <TrendingUp size={12} />,
              label: "累计收益",
              value: `$${formatUSDT(totalEarned)}`,
              sub: "",
              color: C.amber,
              tip: "所有托管订单的历史累计收益",
            },
            {
              icon: <Gift size={12} />,
              label: "今日收益",
              value: `+$${formatUSDT(todayEarned)}`,
              sub: "",
              color: C.green,
              tip: "今日所有托管订单产生的收益",
            },
          ].map(s => (
            <div
              key={s.label}
              className="rounded-lg p-3"
              style={{
                background: "rgba(15,17,23,0.85)",
                backdropFilter: "blur(8px)",
                border: `1px solid ${C.border}`,
              }}
            >
              <div
                className="flex items-center gap-1 text-[9px] uppercase tracking-wider mb-1.5"
                style={{ color: C.dim }}
              >
                {s.icon}
                <span>{s.label}</span>
                <InfoTip text={s.tip} />
              </div>
              <div
                className="mono-num font-bold text-sm"
                style={{ color: s.color }}
              >
                {s.value}
              </div>
              <div className="text-[10px]" style={{ color: C.dim }}>
                {s.sub}
              </div>
            </div>
          ))}
        </div>
      </ScrollReveal>

      {/* Deposit Button */}
      <ScrollReveal
        direction="up"
        distance={14}
        delay={0.08}
        duration={0.4}
        className="px-4 pt-4"
      >
        <button
          className="w-full py-3.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 btn-primary"
          onClick={() => setDepositOpen(true)}
        >
          <ArrowDownLeft size={16} />
          存入托管 · 月化 52%
        </button>
      </ScrollReveal>

      {/* Tabs */}
      <div
        className="flex px-4 mt-4"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        {[
          { id: "active", label: `进行中 (${activeOrders.length})` },
          { id: "history", label: `已赎回 (${completedOrders.length})` },
          { id: "earnings", label: "收益历史" },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className="px-3 py-3 text-xs font-semibold transition-colors"
            style={{
              color: activeTab === tab.id ? C.primary : C.dim,
              borderBottom:
                activeTab === tab.id
                  ? `2px solid ${C.primary}`
                  : "2px solid transparent",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <ScrollReveal
        direction="up"
        distance={20}
        delay={0.12}
        duration={0.45}
        className="px-4 py-4 space-y-4 pb-6"
      >
        <AnimatePresence mode="wait">
          {/* Active Orders */}
          {activeTab === "active" && (
            <motion.div
              key="active"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {/* Rate Info */}
              <div
                className="rounded-lg p-4"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={14} style={{ color: C.primary }} />
                  <span className="font-semibold text-sm">收益规则</span>
                  <div className="live-dot ml-auto" />
                  <span
                    className="text-[10px] mono-num"
                    style={{ color: C.green }}
                  >
                    实时结算
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {[
                    {
                      label: "月化收益率",
                      value: "52%",
                      color: C.primary,
                      tip: "基于平台 AI 量化交易策略的月化收益率",
                    },
                    {
                      label: "日化收益率",
                      value: "1.73%",
                      color: C.amber,
                      tip: "每日按本金的 1.73% 计算收益",
                    },
                    {
                      label: "USDT 分配",
                      value: "70%",
                      color: C.text,
                      tip: "每日收益的 70% 以 USDT 形式发放",
                    },
                    {
                      label: "TSK 分配",
                      value: "30%",
                      color: C.muted,
                      tip: "每日收益的 30% 以 TSK 代币等值发放",
                    },
                  ].map(item => (
                    <div
                      key={item.label}
                      className="rounded p-2.5"
                      style={{
                        background: C.card2,
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      <div
                        className="text-[10px] mb-1 flex items-center"
                        style={{ color: C.dim }}
                      >
                        {item.label}
                        <InfoTip text={item.tip} />
                      </div>
                      <div
                        className="mono-num font-bold text-base"
                        style={{ color: item.color }}
                      >
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {activeOrders.length === 0 ? (
                <div
                  className="rounded-lg p-8 text-center"
                  style={{
                    background: C.card,
                    border: "1px dashed rgba(255,255,255,0.12)",
                  }}
                >
                  <Shield
                    size={32}
                    className="mx-auto mb-3"
                    style={{ color: C.dim }}
                  />
                  <p className="text-sm" style={{ color: C.muted }}>
                    暂无托管订单，点击上方按钮开始托管
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeOrders.map((order, i) => {
                    const isExpanded = expandedOrder === order.id;
                    const earnedPct =
                      (order.totalEarned / (order.amount * 0.52)) * 100;
                    return (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.06 }}
                        className="rounded-lg overflow-hidden"
                        style={{
                          background: C.card,
                          border: "1px solid rgba(34,197,94,0.15)",
                        }}
                      >
                        <div
                          className="h-px"
                          style={{
                            background:
                              "linear-gradient(90deg, transparent, rgba(34,197,94,0.40), transparent)",
                          }}
                        />
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center"
                                style={{
                                  background: "rgba(34,197,94,0.08)",
                                  border: "1px solid rgba(34,197,94,0.20)",
                                }}
                              >
                                <Shield
                                  size={14}
                                  style={{ color: C.primary }}
                                />
                              </div>
                              <div>
                                <div className="font-semibold text-sm mono-num">
                                  #{order.id.slice(-3)}
                                </div>
                                <div
                                  className="text-[10px]"
                                  style={{ color: C.muted }}
                                >
                                  已计息 {order.daysEarned} 天
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div
                                className="mono-num font-bold text-base"
                                style={{ color: C.green }}
                              >
                                <TickingNumber
                                  baseValue={formatUSDT(order.todayEarned)}
                                  prefix="+$"
                                  suffix=""
                                  minInterval={2000}
                                  maxInterval={5000}
                                  incrementRange={[0.01, 0.05]}
                                />
                              </div>
                              <div
                                className="text-[10px]"
                                style={{ color: C.dim }}
                              >
                                今日收益
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mb-3">
                            {[
                              {
                                label: "托管本金",
                                value: `$${formatUSDT(order.amount)}`,
                                color: C.text,
                              },
                              {
                                label: "累计收益",
                                value: `$${formatUSDT(order.totalEarned)}`,
                                color: C.amber,
                              },
                              {
                                label: "月化率",
                                value: order.monthlyRate,
                                color: C.primary,
                              },
                            ].map(s => (
                              <div
                                key={s.label}
                                className="rounded p-2"
                                style={{ background: "rgba(255,255,255,0.02)" }}
                              >
                                <div
                                  className="text-[10px] mb-0.5"
                                  style={{ color: C.dim }}
                                >
                                  {s.label}
                                </div>
                                <div
                                  className="mono-num font-bold text-xs"
                                  style={{ color: s.color }}
                                >
                                  {s.value}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mb-3">
                            <div className="flex justify-between text-[10px] mb-1">
                              <span style={{ color: C.dim }}>月收益进度</span>
                              <span
                                className="mono-num"
                                style={{ color: C.muted }}
                              >
                                {earnedPct.toFixed(1)}%
                              </span>
                            </div>
                            <div
                              className="h-1.5 rounded-full overflow-hidden"
                              style={{ background: "rgba(255,255,255,0.06)" }}
                            >
                              <motion.div
                                className="h-full rounded-full"
                                initial={{ width: 0 }}
                                animate={{
                                  width: `${Math.min(earnedPct, 100)}%`,
                                }}
                                transition={{ duration: 1, ease: "easeOut" }}
                                style={{ background: C.primary }}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              className="flex-1 py-2 rounded-lg text-xs font-semibold"
                              style={{
                                background: C.card2,
                                border: `1px solid ${C.border}`,
                                color: C.muted,
                              }}
                              onClick={() =>
                                setExpandedOrder(isExpanded ? null : order.id)
                              }
                            >
                              <span className="flex items-center justify-center gap-1">
                                {isExpanded ? (
                                  <ChevronUp size={11} />
                                ) : (
                                  <ChevronDown size={11} />
                                )}
                                详情
                              </span>
                            </button>
                            <button
                              className="flex-1 py-2 rounded-lg text-xs font-bold"
                              style={{
                                background: "rgba(34,197,94,0.10)",
                                border: "1px solid rgba(34,197,94,0.25)",
                                color: C.primary,
                              }}
                              onClick={() => setClaimOrder(order)}
                            >
                              领取收益
                            </button>
                            <button
                              className="flex-1 py-2 rounded-lg text-xs font-bold"
                              style={{
                                background: "rgba(239,68,68,0.08)",
                                border: "1px solid rgba(239,68,68,0.20)",
                                color: C.red,
                              }}
                              onClick={() => setRedeemOrder(order)}
                            >
                              赎回
                            </button>
                          </div>
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                key="detail"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div
                                  className="mt-3 pt-3 space-y-2 text-xs"
                                  style={{ borderTop: `1px solid ${C.border}` }}
                                >
                                  <div
                                    className="font-semibold text-xs mb-2"
                                    style={{ color: C.muted }}
                                  >
                                    收益明细
                                  </div>
                                  {[
                                    ["存入时间", order.depositTime],
                                    ["开始计息", order.startEarningTime],
                                    [
                                      "待领 USDT",
                                      `$${formatUSDT(order.rewardSplit.pendingUsdt)}`,
                                    ],
                                    [
                                      "待领 TSK",
                                      `${formatUSDT(order.rewardSplit.pendingAif)} TSK`,
                                    ],
                                    [
                                      "今日 USDT",
                                      `+$${formatUSDT(order.rewardSplit.todayUsdt)}`,
                                    ],
                                    [
                                      "今日 TSK",
                                      `+${formatUSDT(order.rewardSplit.todayAif)} TSK`,
                                    ],
                                  ].map(([k, v]) => (
                                    <div
                                      key={k as string}
                                      className="flex justify-between"
                                    >
                                      <span style={{ color: C.dim }}>{k}</span>
                                      <span
                                        className="mono-num"
                                        style={{ color: C.muted }}
                                      >
                                        {v}
                                      </span>
                                    </div>
                                  ))}
                                  {/* 领取按钮 */}
                                  <button
                                    className="w-full mt-2 py-2 rounded-lg text-xs font-bold"
                                    style={{
                                      background: "rgba(34,197,94,0.10)",
                                      border: "1px solid rgba(34,197,94,0.25)",
                                      color: C.primary,
                                    }}
                                    onClick={() => setClaimOrder(order)}
                                  >
                                    领取待领收益
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              <div
                className="rounded-lg p-4 text-xs space-y-2"
                style={{
                  background: "rgba(34,197,94,0.04)",
                  border: "1px solid rgba(34,197,94,0.10)",
                }}
              >
                <div
                  className="flex items-center gap-2 font-semibold text-sm"
                  style={{ color: C.primary }}
                >
                  <Shield size={14} />
                  资金安全保障
                </div>
                {[
                  "智能合约托管，资金链上透明可查",
                  "AI 量化策略自动止损，最大回撤控制在 5% 以内",
                  "随时可申请赎回，T+1 工作日到账",
                ].map((text, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2"
                    style={{ color: C.muted }}
                  >
                    <div
                      className="w-1 h-1 rounded-full mt-1.5 shrink-0"
                      style={{ background: C.primary }}
                    />
                    {text}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Order History */}
          {activeTab === "history" && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              {completedOrders.length === 0 ? (
                <div className="text-center py-12" style={{ color: C.muted }}>
                  <BarChart2 size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">暂无已完成的托管订单</p>
                </div>
              ) : (
                completedOrders.map((order, i) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className="rounded-lg p-4"
                    style={{
                      background: C.card,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{
                            background: "rgba(255,255,255,0.04)",
                            border: `1px solid ${C.border}`,
                          }}
                        >
                          <Shield size={14} style={{ color: C.dim }} />
                        </div>
                        <div>
                          <div className="font-semibold text-sm mono-num">
                            #{order.id.slice(-3)}
                          </div>
                          <div className="text-[10px]" style={{ color: C.dim }}>
                            已完成 · 共 {order.daysEarned} 天
                          </div>
                        </div>
                      </div>
                      <span
                        className="px-2 py-0.5 rounded text-[10px] font-semibold"
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          color: C.dim,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        已赎回
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[
                        {
                          label: "托管本金",
                          value: `$${formatUSDT(order.amount)}`,
                          color: C.text,
                        },
                        {
                          label: "总收益",
                          value: `$${formatUSDT(order.totalEarned)}`,
                          color: C.green,
                        },
                        {
                          label: "月化率",
                          value: order.monthlyRate,
                          color: C.amber,
                        },
                      ].map(s => (
                        <div
                          key={s.label}
                          className="rounded p-2"
                          style={{ background: "rgba(255,255,255,0.02)" }}
                        >
                          <div
                            className="text-[10px] mb-0.5"
                            style={{ color: C.dim }}
                          >
                            {s.label}
                          </div>
                          <div
                            className="mono-num font-bold text-xs"
                            style={{ color: s.color }}
                          >
                            {s.value}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className="space-y-1.5 text-xs pt-3"
                      style={{ borderTop: `1px solid ${C.border}` }}
                    >
                      {[
                        ["存入时间", order.depositTime],
                        ["赎回时间", order.redeemTime || "-"],
                        [
                          "USDT 收益",
                          `$${formatUSDT(order.rewardSplit.totalUsdt)}`,
                        ],
                        [
                          "TSK 收益",
                          `${formatUSDT(order.rewardSplit.totalAif)} TSK`,
                        ],
                      ].map(([k, v]) => (
                        <div key={k as string} className="flex justify-between">
                          <span style={{ color: C.dim }}>{k}</span>
                          <span className="mono-num" style={{ color: C.muted }}>
                            {v}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))
              )}
            </motion.div>
          )}

          {/* Earnings History */}
          {activeTab === "earnings" && (
            <motion.div
              key="earnings"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              <div
                className="rounded-lg p-3 grid grid-cols-2 gap-3"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <div>
                  <div className="text-[10px] mb-1" style={{ color: C.dim }}>
                    近 10 日 USDT 收益
                  </div>
                  <div
                    className="mono-num font-bold text-base"
                    style={{ color: C.green }}
                  >
                    +$
                    {formatUSDT(
                      EARNINGS_HISTORY.reduce((s, d) => s + d.usdt, 0)
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] mb-1" style={{ color: C.dim }}>
                    近 10 日 TSK 收益
                  </div>
                  <div
                    className="mono-num font-bold text-base"
                    style={{ color: C.primary }}
                  >
                    +
                    {formatUSDT(
                      EARNINGS_HISTORY.reduce((s, d) => s + d.aif, 0)
                    )}{" "}
                    TSK
                  </div>
                </div>
              </div>
              <div
                className="grid grid-cols-3 text-[10px] py-2 px-3 uppercase tracking-wider"
                style={{ color: C.dim }}
              >
                <span>日期</span>
                <span className="text-right">USDT 收益</span>
                <span className="text-right">TSK 收益</span>
              </div>
              <div style={{ borderTop: `1px solid ${C.border}` }} />
              {EARNINGS_HISTORY.map((item, i) => (
                <motion.div
                  key={item.date}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="grid grid-cols-3 text-xs py-2.5 px-3 rounded"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                >
                  <span style={{ color: C.muted }}>{item.date.slice(5)}</span>
                  <span
                    className="text-right mono-num"
                    style={{ color: C.green }}
                  >
                    +${formatUSDT(item.usdt)}
                  </span>
                  <span
                    className="text-right mono-num"
                    style={{ color: C.primary }}
                  >
                    +{formatUSDT(item.aif)} TSK
                  </span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </ScrollReveal>

      <DepositDialog open={depositOpen} onClose={() => setDepositOpen(false)} />
      <RedeemDialog
        open={!!redeemOrder}
        onClose={() => setRedeemOrder(null)}
        order={redeemOrder}
      />
      <ClaimDialog
        open={!!claimOrder}
        onClose={() => setClaimOrder(null)}
        order={claimOrder}
      />
    </div>
  );
}
